/**
 * Task Context
 *
 * React context for tracking async operations (saving, loading, AI generation).
 * Provides centralized task state to show loading states and prevent duplicate actions.
 *
 * Pattern follows ui-components module contexts:
 * - createContext with null default
 * - Provider with useMemo for context value
 * - useCallback for stable function references
 * - Hook with null check and error throw
 */

import React, { createContext, useContext, useState, useCallback, useMemo } from 'react';

/**
 * Task interface
 */
export interface Task {
  id: string;
  description: string;
  status: 'pending' | 'running' | 'complete' | 'error';
}

/**
 * Task context value interface
 */
interface TaskContextValue {
  tasks: Task[];
  startTask: (description: string) => string; // returns task ID
  removeTask: (id: string) => void;
}

/**
 * Task Provider props interface
 */
export interface TaskProviderProps {
  children: React.ReactNode;
}

/**
 * Task Context
 */
const TaskContext = createContext<TaskContextValue | null>(null);

/**
 * Task Provider Component
 *
 * Manages task state and provides API to all child components.
 */
export function TaskProvider({ children }: TaskProviderProps) {
  const [tasks, setTasks] = useState<Task[]>([]);

  const startTask = useCallback((description: string): string => {
    const id = `${Date.now()}-${Math.random()}`;
    const task: Task = {
      id,
      description,
      status: 'running'
    };
    setTasks(prev => [...prev, task]);
    return id;
  }, []);

  const removeTask = useCallback((id: string): void => {
    setTasks(prev => prev.filter(task => task.id !== id));
  }, []);

  const contextValue = useMemo<TaskContextValue>(() => ({
    tasks,
    startTask,
    removeTask
  }), [tasks, startTask, removeTask]);

  return (
    <TaskContext.Provider value={contextValue}>
      {children}
    </TaskContext.Provider>
  );
}

/**
 * Hook to use task context
 *
 * @throws Error when used outside TaskProvider
 */
export function useTaskContext(): TaskContextValue {
  const context = useContext(TaskContext);

  if (!context) {
    throw new Error('useTaskContext must be used within TaskProvider');
  }

  return context;
}
